import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 6 }}>
    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
      {title}
    </Typography>
    <Box sx={{ color: "text.secondary", lineHeight: 1.9, fontSize: "1rem" }}>
      {children}
    </Box>
  </Box>
);

const AccountDeletion = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#f8fafc" }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} color="primary" gutterBottom>
            Account Deletion Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
            At MySchoolling, parent and student accounts are created and managed
            by the respective school or educational institution using the
            MySchoolling platform.
            <br />
            <br />
            Parents and students cannot directly delete their accounts from the
            application because the account is linked to official school records,
            attendance records, academic records, fee records, communication
            history, and other educational data maintained by the school.
          </Typography>
        </Box>

        <Divider sx={{ mb: 8 }} />

        <Section title="How to Request Account Deletion">
          <Typography sx={{ mb: 2 }}>
            If you wish to request deletion of your MySchoolling account, please
            follow these steps:
          </Typography>
          <ol>
            <li>Contact your school's administration office.</li>
            <li>Submit an account deletion request to the school.</li>
            <li>
              The school will review the request and determine whether the
              account can be removed based on its policies and applicable
              record-retention requirements.
            </li>
            <li>
              Upon approval from the school, MySchoolling will process the
              account deletion request.
            </li>
          </ol>
        </Section>

        <Section title="What Data May Be Deleted">
          <Typography sx={{ mb: 2 }}>
            Subject to approval by the school, the following information may be
            deleted:
          </Typography>
          <ul>
            <li>Parent account information</li>
            <li>Login credentials and authentication records</li>
            <li>Parent contact details</li>
            <li>Device associations and notification preferences</li>
          </ul>
        </Section>

        <Section title="Data That May Be Retained">
          <Typography sx={{ mb: 2 }}>
            Certain information may be retained even after an account deletion
            request because it forms part of the school's official records. This
            may include:
          </Typography>
          <ul>
            <li>Student academic records</li>
            <li>Attendance records</li>
            <li>Examination records</li>
            <li>Fee and payment records</li>
            <li>Communication records required by the school</li>
            <li>
              Information required for legal, regulatory, audit, or compliance
              purposes
            </li>
          </ul>
          <Typography sx={{ mt: 2 }}>
            The retention period for such records is determined by the respective
            school and applicable laws and regulations. Schools commonly retain
            educational records for operational, legal, and compliance purposes.
          </Typography>
        </Section>

        <Section title="Parent and Student Data">
          <Typography>
            MySchoolling acts as a technology platform for schools. Much of the
            information displayed within the application originates from and is
            managed by the educational institution. Parents and students should
            contact their school directly regarding requests related to
            modification, correction, or deletion of educational records.
            Educational institutions are typically responsible for managing
            student and parent information and determining retention requirements.
          </Typography>
        </Section>

        <Section title="Contact Us">
          <Typography sx={{ mb: 1 }}>
            For questions regarding account deletion requests, please contact:
          </Typography>
          <Typography>
            Email:{" "}
            <a href="mailto:support@myschoolling.com">
              support@myschoolling.com
            </a>
          </Typography>
          <Typography>
            Website:{" "}
            <a href="https://myschoolling.com" target="_blank" rel="noreferrer">
              https://myschoolling.com
            </a>
          </Typography>
        </Section>

        <Divider sx={{ mt: 8 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          Last Updated: June 2026
        </Typography>
      </Container>
    </Box>
  );
};

export default AccountDeletion;
